import React, { useEffect, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { overlayGuard } from '@/contexts/PrivacyGuardContext/overlayguard';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { IS_ANDROID } from '@/utils/platform';
import { logInfo } from '@/utils/logHandling';

type Props = {
  isAuthenticated: boolean;
  children: React.ReactNode;
};
// RAA-1513 This provider triggers the privacy screen when the app goes to background on Android devices.
// blur/focus and change events are both handled because Android devices inconsistently fire one or the other.
// didEnterPrivacy guards against double-navigation when both events fire.
export const PrivacyGuardProvider = ({ isAuthenticated, children }: Props) => {
  const didEnterPrivacy = useRef(false);
  const navigation =
    useNavigation<StackNavigationProp<PatientStackParamList>>();

  useEffect(() => {
    if (!isAuthenticated || !IS_ANDROID) {
      return;
    }

    const enterPrivacy = () => {
      if (didEnterPrivacy.current) return;
      if (overlayGuard.isActive()) {
        return;
      }
      didEnterPrivacy.current = true;
      navigation.navigate('Privacy');
      logInfo('Entered privacy screen');
    };

    const exitPrivacy = () => {
      if (!didEnterPrivacy.current) {
        return;
      }
      didEnterPrivacy.current = false;
      navigation.canGoBack() && navigation.goBack();
      logInfo('Exited privacy screen');
    };

    const onBlur = () => {
      enterPrivacy();
    };

    const onFocus = () => {
      exitPrivacy();
    };

    const onAppStateChange = (nextState: AppStateStatus) => {
      if (nextState === 'background' || nextState === 'inactive') {
        enterPrivacy();
      } else if (nextState === 'active') {
        exitPrivacy();
      }
    };

    const blurSub = AppState.addEventListener('blur', onBlur);
    const focusSub = AppState.addEventListener('focus', onFocus);
    const changeSub = AppState.addEventListener('change', onAppStateChange);

    return () => {
      blurSub.remove();
      focusSub.remove();
      changeSub.remove();
    };
  }, [isAuthenticated]);

  return <>{children}</>;
};
